import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IDateRange, IOption } from  '@/interfaces/insightsp2';
import { BuildPath } from  '@/helpers/Paths';
import SelectLabel from './SelectLabel';
import  '@/css/customSelect.scss';



type Props = {
    options:IOption[], 
    optionsChange:(options:IOption[]) => void,
    multi:boolean,
    showCalendar:boolean,
    label?:string,
    customDate?:Date[],
    selectedDates?:IDateRange,
    placeholder?:string,
    showDateLabel?:boolean,
    defaultSelectedIndex?:number
}

const CustomSelect = forwardRef((
    {
        options, 
        optionsChange, 
        multi, 
        showCalendar, 
        label, 
        customDate, 
        selectedDates, 
        placeholder, 
        showDateLabel,
        defaultSelectedIndex
    }: Props, ref) => {
    
    const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
    const [menu, setMenu] = useState<any>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const dropDownRef = useRef(null);

    // Detect click outside menu
    useEffect(() => {

        // Close menu if clicked away
        function clickListener(e:MouseEvent){

            if (!inMenu(e) && isOpen){
                menuClose();
            }
        }

        window.addEventListener('click', clickListener); 

        return () => {
            window.removeEventListener('click', clickListener);
        }

    }, [isOpen])

    useEffect(() => {

        // If all selected clear and dont pass up
        if (selectedOptions.find(x => x.value === null)){
            setSelectedOptions([]);
            optionsChange([]);
        }
        else if (selectedOptions.length){
            optionsChange(selectedOptions);
        }
        
    }, [selectedOptions]);

    // Expose function to clear from above
    useImperativeHandle(ref, () => {
        return {
            clear
        };
    });

    function toggleGroup(selected:IOption){

        // If multiple clear all and add
        if (!multi){
            setSelectedOptions([selected]);

            // Close if not multi
            menuClose();

            setTimeout(() => {
                setMenu(null)
            }, 100);

            return;
        }

        if (!selectedOptions.filter(x => x.value === selected.value).length){
            setSelectedOptions([...selectedOptions, selected])
        }
        else{
            setSelectedOptions(selectedOptions.filter(x => x.value !== selected.value));
        }
    }

    function clear():void{
        setSelectedOptions([]);
    }

    function menuOpen():void{

        setIsOpen(true);

        setMenu({
            'display':'block'
        });
    }

    function menuClose():void{

        setIsOpen(false);

        // Reset scroll position
        if (dropDownRef.current){
            dropDownRef.current.scrollTop = 0;
        }

        setMenu({
            'display':'none'
        });
    }

    function inMenu(e):boolean{
        return Array.from(document.querySelectorAll('.custom-select-container')).some(el => el.contains(e.target));
    }
  
    const Icon = ({current, idx} : {current:IOption, idx:number}):JSX.Element =>{
         
        // Default selected
        if (defaultSelectedIndex === idx && !selectedOptions.length){
            return (
                <img src={`${BuildPath()}/images/insightsp2/checkmark.svg`} alt="selected" />
            )
        }

        // Click selected
        if (selectedOptions.filter(x => x.value === current.value).length){
            return (
                <img src={`${BuildPath()}/images/insightsp2/checkmark.svg`} alt="selected" />
            )
        }

        return null;
    }

    return (
        <div className="custom-select-container" onClick={menuOpen} >

            <div className={`selected ${isOpen ? 'highLight' : ''}`}>
               <SelectLabel 
                    selectedOptions={selectedOptions} 
                    label={label} 
                    placeholder={placeholder} 
                    customDate={customDate} 
                    showDateLabel={showDateLabel} 
                    selectedDates={selectedDates}
                />
            </div>

            {options && options.length > 0 && 
                <img alt="" src={`${BuildPath()}/images/insightsp2/DropDownIcon.svg`} className="dropdown-indicator" />
            }

            {options && options.length > 0 && 
                <div className="options" style={menu} ref={dropDownRef}>
          
                    {multi && 
                        <div className="control">
                            <span>{!selectedOptions.length ? "All" : (selectedOptions.length)} groups selected</span>
                            <span onClick={clear}>Clear</span>
                        </div>
                    }

                    {!showCalendar && 
                        <ul>
                            {options.map((group, idx) => {
                                return(
                                    <li key={group.value} onClick={() => toggleGroup(group)}>
                                        {group.label}
                                        <Icon current={group} idx={idx} />
                                    </li>
                                )
                            })}
                        </ul>
                    }
                </div>
            }

        </div>
    );
});

export default CustomSelect;